import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS } from '@contentful/rich-text-types';
import { readableColor } from 'polished';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import { fixPropWarnings } from '../utils/fixPropWarnings';

import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';
import ActionController from './ActionController';

const BannerBackground = styled.div.withConfig(fixPropWarnings('background'))`
  background-color: ${({ background }) => themeGet(`colors.${background}`)};
  color: ${({ theme, background }) => readableColor(
    theme.colors[background],
    theme.colors.black,
    theme.colors.black,
  )};
  padding: 40px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding: 60px 0;
  }
`;

const Heading = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading5.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading5.fontSize')};
  line-height: ${themeGet('typography.mobile.heading5.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading5.letterSpacing')};
  margin: 0 0 10px 0;

  ${BannerBackground}.large & {
    font-size: ${themeGet('typography.mobile.heading2.fontSize')};
    line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
    margin: 0 0 20px 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading4.fontSize')};
    line-height: ${themeGet('typography.desktop.heading4.lineHeight')};

    ${BannerBackground}.large & {
      font-size: ${themeGet('typography.desktop.heading2.fontSize')};
      line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
      margin: 0 0 30px 0;
    }
  }
`;

const Wrapper = styled.div.withConfig(fixPropWarnings('isEven'))`
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  align-items: ${({ isEven }) => (isEven ? 'flex-start' : 'flex-end')};

  ${BannerBackground}.large & {
    padding: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: ${({ isEven }) => (isEven ? 'row' : 'row-reverse')};
    align-items: center;
    justify-content: space-between;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    align-items: flex-start;

    max-width: 1016px;
    margin: 0 auto;
    padding: 0 28px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    max-width: 1072px;
    ${BannerBackground}.large & {
      max-width: 1160px;
    }
  }
`;

const Content = styled.div.withConfig(fixPropWarnings('isEven'))`
  max-width: 496px;
  margin: 25px 0 0;
  text-align: ${({ isEven }) => (isEven ? 'left' : 'right')};

  ${BannerBackground}.large & {
    text-align: left;
    margin: 20px 20px 0 20px;

    .mobileHeader {
      display: block;
    }

    ul {
      list-style: disc;
      margin-left: 15px;
    }

    .desktopHeader {
      display: none;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: ${({ isEven }) => (isEven ? '0 0 0 30px' : '0 30px 0 0')};
    text-align: left;

    ${BannerBackground}.large & {
      .mobileHeader {
        display: none;
      }

      .desktopHeader {
        display: block;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: ${({ isEven }) => (isEven ? '40px 0 0 40px' : '40px 40px 0 0')};

    ${BannerBackground}.large & {
      margin: 0;
      max-width: 535px;

      .mobileHeader {
        display: none;
      }

      .desktopHeader {
        display: block;
      }

      ul {
        list-style: disc;
      }
    }
  }
`;

const BodyCopy = styled.p`
 font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
  margin: 0 0 30px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body2.fontSize')};
    line-height: ${themeGet('typography.desktop.body2.lineHeight')};
  }
`;

const Media = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 335px;
  max-height: 100%;
  width: 100%;

  ${BannerBackground}.large & {
    max-width: none;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    min-width: 280px;
    max-width: 280px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    max-width: 392px;
    max-height: 260px;

    ${BannerBackground}.large & {
      max-width: 492px;
      max-height: 286px;
      margin-left: 88px;
    }
  }
`;

const AnimationAsset = styled.img.withConfig(fixPropWarnings('isEven'))`
  position: absolute;
  top: ${({ isEven }) => (isEven ? '0px' : '-28%')};
  left: ${({ isEven }) => (isEven ? '-25%' : '25%')};
`;

const BannerCTA = styled(ActionController)`
  color: inherit;
  font-weight: 600;
`;

const Image = styled(GatsbyImage)`
  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    border-radius: 12px;
  }
`;
const BannerOptions2 = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading className="mobileHeader">{children}</Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading className="mobileHeader">{children}</Heading>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy>{children}</BodyCopy>,
  },
};

const BannerOptions = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading className="desktopHeader">{children}</Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading className="desktopHeader">{children}</Heading>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy>{children}</BodyCopy>,
  },
};

const Banner = ({ data, isEven, className, variant }) => {
  if (!data) return null;
  const {
    sectionId,
    headingRTF,
    bodyRTF,
    button,
    backgroundColor,
    imageGallery,
  } = data;

  return (
    <BannerBackground
      id={sectionId}
      background={backgroundColor}
      className={[className, variant].join(' ')}
    >
      <SectionContainer fluid>
        <Wrapper isEven={isEven}>
          {variant === 'large' && (
            <Content isEven={isEven}>
              <RichText document={headingRTF} options={BannerOptions2} />
            </Content>
          )}
          <Media>
            {(imageGallery || [])
              .map(image => [image, getImage(image?.asset?.localImage)])
              .map(([image, gatsbyImage]) => {
                switch (image.asset.mimeType) {
                  case 'image/gif':
                    return (
                      <AnimationAsset
                        key={image.id}
                        src={image.asset.localImage.publicURL}
                        alt={image.altText}
                        isEven={isEven}
                      />
                    );
                  default:
                    return (
                      gatsbyImage && (
                        <Image
                          key={image.id}
                          image={gatsbyImage}
                          alt={image.altText || ''}
                        />
                      )
                    );
                }
              })}
          </Media>
          <Content isEven={isEven}>
            <RichText document={headingRTF} options={BannerOptions} />
            <RichText document={bodyRTF} options={BannerOptions} />
            {button && <BannerCTA data={button} />}
          </Content>
        </Wrapper>
      </SectionContainer>
    </BannerBackground>
  );
};

Banner.propTypes = {
  data: PropTypes.shape().isRequired,
  isEven: PropTypes.bool,
  variant: PropTypes.string,
  className: PropTypes.string,
};

Banner.defaultProps = {
  isEven: false,
  variant: null,
  className: null,
};

export default Banner;
